import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { filterNil } from '@datorama/akita';
import { filterNull } from '@lycoris-nubila/ngx-lycoris';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import {
	map,
	mapTo,
	shareReplay,
	startWith,
	switchMap,
	tap,
} from 'rxjs/operators';

import { OrganizationPropertyName } from 'organization/model/organization-property-name';
import { AppService } from 'presentation/app.service';
import { UserRole } from 'user/model/user-role';
import { UserApplicationPort } from 'user/port/user-application.port';
import { IUser } from 'user/user';

@Injectable()
@UntilDestroy()
export class AppToolbarController {
	public i18n = 'presentation.app-toolbar';

	public title$: Observable<string>;
	public logoUrl$: Observable<string>;
	public hasUpdate$: Observable<boolean>;
	public sessionUser$: Observable<IUser>;
	public settingsUrl$: Observable<string[]>;
	public focusModeEnabled$: Observable<boolean>;
	public showCoachingBooth$: Observable<boolean>;

	constructor(
		private _swUpdates: SwUpdate,
		private _appService: AppService,
		private _translateService: TranslateService,
		@Inject(DOCUMENT) private _document: Document,
		private _userApplicationPort: UserApplicationPort
	) {
		this._initObservables();
	}

	public closeSession() {
		this._userApplicationPort.closeUserSession();
	}

	public updateLastInteraction() {
		this._appService.updateLastInteraction();
	}

	public updateFocusMode(focusModeEnabled: boolean) {
		this._appService.updateFocusMode(focusModeEnabled);
	}

	private _initObservables() {
		this.focusModeEnabled$ = this._appService.focusModeEnabled$;

		this.sessionUser$ = this._userApplicationPort
			.observeSessionUser()
			.pipe(shareReplay({ bufferSize: 1, refCount: true }));

		this.logoUrl$ = this.sessionUser$.pipe(
			filterNull(),
			switchMap((user) => user.organization$),
			map((org) => org?.logoUrl),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.showCoachingBooth$ = this.sessionUser$.pipe(
			filterNull(),
			switchMap((user) =>
				user.organization$.pipe(
					map(
						(org) =>
							user.isCoachee &&
							(!org ||
								org.isPropertyTrue(
									OrganizationPropertyName.COACHING_BOOTH_ENABLED,
									true
								))
					)
				)
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.hasUpdate$ = this._swUpdates.available.pipe(
			tap((version: any) => {
				if (version.available.appData.forceUpdate) {
					this._document.location.reload();
				}
			}),
			mapTo(true),
			startWith(false),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.title$ = this.sessionUser$.pipe(
			switchMap((user) => user?.organization$),
			switchMap((org) =>
				org
					? of(org.name)
					: this._translateService.stream('presentation.app.toolbar.title')
			)
		);

		this.settingsUrl$ = this._userApplicationPort.observeSessionUser().pipe(
			filterNil,
			map((data) =>
				data.role === UserRole.COACH ? ['/settings/coach'] : ['/settings/coachee']
			)
		);
	}

	public markUserHelpConversationAsShown() {
		this._userApplicationPort.markUserHelpConversationAsShown();
	}
}
